import {useMutation} from "@apollo/client";
import pluralize from "pluralize";
import toast from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {
  GQL_BULK_END_ACTIVE_MAINTENANCE_WINDOWS,
  GQL_MAINTENANCE_OCCURRENCES_LIST,
  GQL_MAINTENANCE_SCHEDULES_LIST,
} from "@/lib/queries/maintenance.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {MaintenanceOccurrence} from "@/lib/types/maintenance.ts";
import {MaintenanceItemList} from "@/pages/maintenance/maintenance-dashboard/components/MaintenanceItemList/MaintenanceItemList.tsx";

export interface BulkEndActiveWindowsDialogProps {
  occurrences: MaintenanceOccurrence[];
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkEndActiveWindowsDialog = ({
  occurrences,
  show,
  onClose,
  onSuccess,
}: BulkEndActiveWindowsDialogProps) => {
  const [bulkEndActiveWindows, {loading}] = useMutation(GQL_BULK_END_ACTIVE_MAINTENANCE_WINDOWS, {
    refetchQueries: [GQL_MAINTENANCE_OCCURRENCES_LIST, GQL_MAINTENANCE_SCHEDULES_LIST],
  });

  const count = occurrences.length;

  const handleConfirm = async () => {
    const response = await bulkEndActiveWindows({
      variables: {occurrenceIds: occurrences.map((o) => o.id)},
    });
    if (response.data?.response?.status === ResponseStatus.SUCCESS) {
      onSuccess();
      onClose();
    } else {
      toast.error(response.data?.response?.message || "Failed to end active Maintenance Windows.");
    }
  };

  return (
    <ActionDialog
      show={show}
      title="End Active Windows"
      confirmBtnText="Confirm"
      cancelBtnText="Cancel"
      loading={loading}
      onCancel={onClose}
      onConfirm={handleConfirm}
      size="lg"
    >
      <p className="pulseTextBody mb-4">
        Are you sure you want to end the selected active maintenance windows?{" "}
        <span className="pulseTextBodyBold">{pluralize("selected window", count, true)}</span> will end immediately and
        monitoring will resume for all linked checks.
      </p>
      <MaintenanceItemList
        items={occurrences.map((occurrence) => ({
          id: occurrence.id,
          name: occurrence.schedule.name,
          services_count: occurrence.services.length,
        }))}
      />
      <Alert variant="maintenance" callout="Attention" className="mt-3">
        This will end the current active cycle. Any recurring schedules will run as configured.
      </Alert>
    </ActionDialog>
  );
};
